"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, CircleCheck as CheckCircle2, Fan, HardHat, House as Home, Landmark, Layers, Server, ShieldAlert, ShieldCheck, Sun, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const sectorItems = [{
  id: "roofing",
  title: "Roofing contractors",
  badge: "Residential & Commercial",
  summary: "Safe perimeter access, chimney scaffolds, and temporary weather protection built to your programme timeline.",
  highlight1: "Handover inspections before work begins",
  highlight2: "Temporary weather protection & edge loading bays",
  sectorUrl: "/RoofingContractorsPage",
  serviceName: "Roofing scaffolding",
  serviceUrl: "/RoofingScaffoldingPage",
  iconName: "Home"
}, {
  id: "solar",
  title: "Solar installers",
  badge: "Domestic & Commercial PV",
  summary: "Rapid-turnaround access towers and gutter-line edge protection to keep solar installation teams moving without delay.",
  highlight1: "Strict residential disruption management",
  highlight2: "Integrated panel hoist & material staging points",
  sectorUrl: "/SolarInstallersPage",
  serviceName: "Solar access scaffolding",
  serviceUrl: "/SolarScaffoldingPage",
  iconName: "Sun"
}, {
  id: "main-contractors",
  title: "Main contractors & developers",
  badge: "New Build & Heavy Works",
  summary: "Full site access schemes with phased adaptations, strict RAMS compliance, and weekly statutory inspections.",
  highlight1: "Dedicated site supervisor & programme coordination",
  highlight2: "TG20:21 compliant designs & handover certificates",
  sectorUrl: "/MainContractorsDevelopersPage",
  serviceName: "New build scaffolding",
  serviceUrl: "/NewBuildScaffoldingPage",
  iconName: "Building2"
}, {
  id: "councils",
  title: "Councils & public sector",
  badge: "Social Housing & Civic",
  summary: "Vetted, safety-led scaffolding for public realm improvements, social housing refurbishments, and civic assets.",
  highlight1: "Occupied building protocols & public protection",
  highlight2: "CHAS & NASC accredited teams",
  sectorUrl: "/CouncilsAndPublicSectorPage",
  serviceName: "Refurbishment scaffolding",
  serviceUrl: "/RefurbishmentScaffoldingPage",
  iconName: "Landmark"
}, {
  id: "facilities",
  title: "Property & facilities management",
  badge: "Planned & Reactive Maintenance",
  summary: "Reliable access solutions for cyclical maintenance, facade repairs, emergency repairs, and high-level inspections.",
  highlight1: "Minimal tenant disruption & secure site hoarding",
  highlight2: "Out-of-hours erection & rapid dismantling",
  sectorUrl: "/PropertyFacilitiesManagementPage",
  serviceName: "Specialist access scaffolding",
  serviceUrl: "/SpecialistAccessScaffoldingPage",
  iconName: "Wrench"
}, {
  id: "demolition",
  title: "Demolition contractors",
  badge: "Heavy Industrial & Strip-Out",
  summary: "Heavy-duty crash decks, debris screening, and sequence-phased dismantle scaffolds for controlled structural deconstruction.",
  highlight1: "Engineered load-rated protection screens & crash decks",
  highlight2: "Rigorous dust containment & drop-zone management",
  sectorUrl: "/DemolitionContractorsPage",
  serviceName: "Industrial scaffolding",
  serviceUrl: "/IndustrialScaffoldingPage",
  iconName: "ShieldAlert"
}, {
  id: "hvac",
  title: "Air conditioning & HVAC contractors",
  badge: "Plant Access & Rooftop Units",
  summary: "Engineered rooftop staging, chiller gantry platforms, and crane-lift access for high-level mechanical plant installations.",
  highlight1: "Point-load distribution over delicate roof membranes",
  highlight2: "Dedicated crane staging bays & heavy plant access",
  sectorUrl: "/AirConditioningHVACContractorsPage",
  serviceName: "Specialist access scaffolding",
  serviceUrl: "/SpecialistAccessScaffoldingPage",
  iconName: "Fan"
}, {
  id: "datacentres",
  title: "Data centres",
  badge: "Mission-Critical Infrastructure",
  summary: "High-security, dust-controlled access and internal overhead service platforms for active and new-build data facilities.",
  highlight1: "Security-cleared operatives & zero unplanned outage protocols",
  highlight2: "Cleanroom-compliant equipment & protective floor matting",
  sectorUrl: "/DataCentresPage",
  serviceName: "Industrial scaffolding",
  serviceUrl: "/IndustrialScaffoldingPage",
  iconName: "Server"
}, {
  id: "heritage",
  title: "Heritage & listed buildings",
  badge: "Conservation & Specialist Care",
  summary: "Non-intrusive, free-standing access scaffolds, timber protection buffers, and temporary roofs safeguarding historic fabric.",
  highlight1: "Zero-contact clamping & non-destructive masonry ties",
  highlight2: "Temporary weather-tight enclosures & conservation staging",
  sectorUrl: "/HeritageAndListedBuildingsPage",
  serviceName: "Specialist access scaffolding",
  serviceUrl: "/SpecialistAccessScaffoldingPage",
  iconName: "Landmark"
}];
const alsoServingSectors = [{
  name: "Civil Engineering"
}, {
  name: "Builders"
}, {
  name: "Domestic & Residential Customers"
}, {
  name: "Property Developers"
}, {
  name: "Theatre & Stage"
}, {
  name: "Site Enabling Contractors"
}, {
  name: "Churches & Places of Worship"
}];
export default function SectorGrid() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "Home":
        return <Home className="size-5 text-primary" />;
      case "Sun":
        return <Sun className="size-5 text-primary" />;
      case "Building2":
        return <Building2 className="size-5 text-primary" />;
      case "Landmark":
        return <Landmark className="size-5 text-primary" />;
      case "Wrench":
        return <Wrench className="size-5 text-primary" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-5 text-primary" />;
      case "Fan":
        return <Fan className="size-5 text-primary" />;
      case "Server":
        return <Server className="size-5 text-primary" />;
      default:
        return <HardHat className="size-5 text-primary" />;
    }
  };
  return <section id="sectorsolutionsgrid" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-muted border border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Targeted Trade Solutions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Scaffolding access aligned with how your trade works
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            Every trade has distinct site constraints, inspection obligations, and programme pressures. We provide clear scheduling, certified handovers, and reliable site coordination across the South East.
          </p>
        </div>

        {/* 9-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sectorItems.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.45,
          delay: index * 0.06
        }} className="flex flex-col">
              <Card className="h-full flex flex-col justify-between border-border bg-card shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <CardHeader className="gap-3 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      {getIcon(item.iconName)}
                    </div>
                    <Badge variant="outline" className="text-[11px] font-medium text-muted-foreground">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {item.summary}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0 pb-6 flex-1 flex flex-col justify-between gap-4">
                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <div className="flex items-start gap-2 text-xs text-foreground/90 font-medium">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{item.highlight1}</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-foreground/90 font-medium">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{item.highlight2}</span>
                    </div>
                  </div>

                  {/* Specific Service Link Callout */}
                  <div className="p-3 rounded-lg bg-muted/60 border border-border/80 flex items-center justify-between text-xs">
                    <div className="text-muted-foreground">
                      <span className="text-foreground font-semibold">Service focus:</span>{" "}
                      {item.serviceName}
                    </div>
                    <Link to={item.serviceUrl} className="font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0 ml-2">
                      Details
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </CardContent>

                <CardFooter className="pt-3 border-t border-border bg-muted/20 flex items-center justify-between rounded-b-xl">
                  <Button variant="default" size="sm" asChild className="w-full sm:w-auto">
                    <Link to={item.sectorUrl} className="flex items-center justify-center gap-2">
                      <span>View sector</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <span className="hidden sm:inline-block text-xs text-muted-foreground font-medium">
                    NASC & CHAS compliant
                  </span>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Also Serving Section Block */}
        <div className="mt-12 p-6 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 pb-4 border-b border-border/70">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 text-primary">
                <Layers className="size-4" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground font-default">
                  Also serving across all project scales
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground font-sans">
                  We provide tailored scaffolding schemes for specialist sectors without dedicated landing pages.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" asChild className="self-start md:self-auto shrink-0">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5">
                <span>General enquiry</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {alsoServingSectors.map((sector, idx) => <Link key={sector.name} data-index={idx} to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/70 hover:bg-primary/10 border border-border hover:border-primary/40 text-foreground transition-colors group">
                <span>{sector.name}</span>
                <ArrowRight className="size-3 text-muted-foreground group-hover:text-primary transition-colors" />
              </Link>)}
          </div>
        </div>

        {/* Bottom Context Banner */}
        <div className="mt-8 p-6 rounded-xl bg-muted/40 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground font-default">
              Need multi-trade or specialized phased scaffolding?
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              We coordinate safe access staging for mixed-occupancy and fast-track refurbishment programmes across the South East.
            </p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
              <span>Request project quote</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}